import React, { useState, useEffect, useCallback } from "react";
import { Route, useRouteMatch } from "react-router-dom";
import { Tabs, message } from "antd";
import axios from "axios";
import forEach from "lodash/forEach";
import UserParametersContainer from "../UserParametersContainer/UserParametersContainer";
import containerStyle from "./MainContainerStyles";
import RolesTabUsers from "../../components/RolesTabUsers/RolesTabUsers";
import AdministrationContainer from "../AdministrationContainer/AdministrationContainer";
import RoleParametersContainer from "../RoleParametersContainer/RoleParametersContainer";
import PermissionsDescription from "../../components/PermissionsDescription/PermissionsDescription";
import {
  createUserRoute,
  getPermissionsRoute,
  getRequirementsRoute,
  getRolesRoute,
  getUserByIdRoute,
  getUsersPageableSearchRoute,
  getUsersRoute,
} from "../../lib/rolesRoutes";
import LocalStorageService from "../../../../services/LocalStorageService";
import compareStringsWithPoints from "../../../../lib/utils/compareStringWithPoints";
import { hasAccess } from "../../hasAccess";
import {
  MENU_USER_USERMANAGEMENT,
  MENU_USER_USERMANAGEMENT_WRITE,
  USERMANAGEMENT_ROLES,
  USERMANAGEMENT_USER_PERMISSIONS,
  USERMANAGEMENT_USER_ROLES,
} from "../../../../lib/const/permissions";

const { TabPane } = Tabs;

export default function MainContainer() {
  const { url } = useRouteMatch();
  const [userTablePage, setUserTablePage] = useState(0);
  const [selectedUserToAssign, setSelectedUserToAssign] = useState(0);
  const [selectedUserToBlock, setSelectedUserToBlock] = useState(0);
  const [selectedAdministrationTab, setSelectedAdministrationTab] = useState(1);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState();
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [requirements, setRequirements] = useState({ description: [], pattern: "" });
  const [requirementsLoading, setRequirementsLoading] = useState(false);

  const getUsers = async (page, size) => {
    setUsersLoading(true);
    const usersRoute = getUsersRoute(page, size);
    const response = axios.get(usersRoute);
    const { data } = await response;
    setUsersLoading(false);
    return data;
  };

  const fetchUsers = () => {
    getUsers(userTablePage).then((res) => {
      setUsersTotal(res.totalElements);
      setUsers([...res.content]);
    });
  };

  const getPermissions = async (page, size) => {
    setPermissionsLoading(true);
    const response = axios.get(getPermissionsRoute(page, size));
    const { data } = await response;
    setPermissionsLoading(false);
    return data;
  };

  const fetchPermissions = () => {
    getPermissions().then((res) => {
      setPermissions([...res.content].sort((a, b) => compareStringsWithPoints(a.alias, b.alias)));
    });
  };

  const getRoles = async (page, size) => {
    const response = axios.get(getRolesRoute(page, size));
    const { data } = await response;
    return data;
  };

  const fetchRoles = () => {
    getRoles().then((res) => {
      setRoles(
        [...res.content].sort((a, b) => {
          if (a.alias > b.alias) {
            return 1;
          }
          if (a.alias < b.alias) {
            return -1;
          }
          return 0;
        })
      );
    });
  };

  const getUserById = useCallback(async (userId) => {
    const response = axios.get(getUserByIdRoute(userId));
    const { data } = await response;
    return data;
  }, []);

  const fetchRequirements = async () => {
    setRequirementsLoading(true);
    const response = axios.get(getRequirementsRoute());
    const { data } = await response;
    setRequirements(data);
    setRequirementsLoading(false);
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchRequirements();
  }, []);

  const handleChangeAdministrationTab = (tab) => {
    setSelectedAdministrationTab(tab);
  };

  const handleChangeUsersPage = (page) => {
    setUserTablePage(page - 1);
  };

  const handleCreateUser = (values) => {
    const { login, password, firstName, middleName, lastName, email } = values;
    return axios
      .post(createUserRoute(), {
        login,
        password,
        tenantId: LocalStorageService.getTenantId(),
        userType: "internal",
        firstName,
        middleName,
        lastName,
        email,
      })
      .then(() => {
        message.success("Пользователь создан");
        fetchUsers();
        return { status: true };
      })
      .catch((error) => {
        return { status: false, error };
      });
  };

  const handleDeleteUser = (id) => {
    axios.delete(getUserByIdRoute(id)).then(() => fetchUsers());
  };

  const handleSelectUserToAssign = (userId) => {
    setSelectedUserToAssign(userId);
  };

  const handleSelectUserToBlock = (userId) => {
    setSelectedUserToBlock(userId);
  };

  const handlePageableSearch = async (value) => {
    let query = "";
    forEach(value, (val, key) => {
      if (val) {
        query = query.concat(`${key}=${val}&`);
      }
    });
    const response = axios.get(getUsersPageableSearchRoute(query));
    const { data } = await response;
    setUsersTotal(data.totalElements);
    setUsers([...data.content]);
  };

  const renderRoleSettingsTabs = () => {
    const renderTab = (name) => {
      const style = {
        width: "218px",
        textAlign: "center",
      };
      return <div style={style}>{name}</div>;
    };

    return (
      <Tabs defaultActiveKey="1">
        {hasAccess(MENU_USER_USERMANAGEMENT, MENU_USER_USERMANAGEMENT_WRITE) && (
          <TabPane tab={renderTab("Пользователи")} key="1">
            <RolesTabUsers
              users={users}
              roles={roles}
              permissions={permissions}
              usersLoading={usersLoading}
              handleDeleteUser={handleDeleteUser}
              handleChangeUsersPage={handleChangeUsersPage}
              handleCreateUser={handleCreateUser}
              handlePageableSearch={handlePageableSearch}
              usersTotal={usersTotal}
              fetchUsers={fetchUsers}
              userTablePage={userTablePage}
              requirements={requirements}
            />
          </TabPane>
        )}

        {hasAccess(
          MENU_USER_USERMANAGEMENT_WRITE,
          USERMANAGEMENT_USER_ROLES,
          USERMANAGEMENT_USER_PERMISSIONS
        ) && (
          <TabPane tab={renderTab("Администрирование")} key="2">
            <AdministrationContainer
              selectedAdministrationTab={selectedAdministrationTab}
              handleChangeAdministrationTab={handleChangeAdministrationTab}
              handleSelectUserToAssign={handleSelectUserToAssign}
              handleSelectUserToBlock={handleSelectUserToBlock}
              selectedUserToAssign={selectedUserToAssign}
              selectedUserToBlock={selectedUserToBlock}
              users={users}
              roles={roles}
              permissions={permissions}
              permissionsLoading={permissionsLoading}
              getUsers={getUsers}
            />
          </TabPane>
        )}
        {hasAccess(MENU_USER_USERMANAGEMENT_WRITE, USERMANAGEMENT_ROLES) && (
          <TabPane tab={renderTab("Роли")} key="3">
            <RoleParametersContainer
              roles={roles}
              permissions={permissions}
              fetchRoles={fetchRoles}
            />
          </TabPane>
        )}
        {hasAccess(MENU_USER_USERMANAGEMENT_WRITE, MENU_USER_USERMANAGEMENT) && (
          <TabPane tab={renderTab("Привилегии")} key="4">
            <PermissionsDescription permissions={permissions} />
          </TabPane>
        )}
      </Tabs>
    );
  };

  return (
    <div className="roles-module container_height" style={containerStyle}>
      <Route exact path={`${url}`}>
        <h1>Система управления пользователями</h1>
        {renderRoleSettingsTabs()}
      </Route>
      {hasAccess(MENU_USER_USERMANAGEMENT, MENU_USER_USERMANAGEMENT_WRITE) && (
        <Route path={`${url}/:userId/profile`}>
          <UserParametersContainer
            permissions={permissions}
            permissionsLoading={permissionsLoading}
            getUserById={getUserById}
            handleDeleteUser={handleDeleteUser}
            requirements={requirements}
            requirementsLoading={requirementsLoading}
          />
        </Route>
      )}
    </div>
  );
}
